import React from 'react';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import DoneIcon from '@mui/icons-material/Done';
import AddAroundIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SvgReport from './SvgReport';
import SvgCoach from './SvgCoach';
import SvgPlan from './SvgPlan';
import SvgRewrite from './SvgRewrite';
import { RESUME_CONSULT_SERVICE_ID } from '@/constants/component-id';
import setAlertValue from '@/components/alert';
import { trackClickEvent } from '@/utils/site-track';
import { getResumeConsultGoods } from '@/api/sales';

export type ServiceType = {
  id: string;
  name: string;
  price: number;
  promotion: { discount: number; sale: number };
  description: string;
  type: string;
  content: string[];
};

const GOODS_IMAGE = [
  <SvgReport key={0} />,
  <SvgCoach key={1} />,
  <SvgPlan key={2} />,
  <SvgRewrite key={3} />,
];

const Loading = (props: { width?: string }) => {
  const { width } = props;

  return (
    <div id={RESUME_CONSULT_SERVICE_ID} className={`${width} mx-auto`}>
      <div className="overflow-x-auto tablet:overflow-x-visible">
        <div className="flex justify-between gap-x-6 min-w-[1000px]">
          <Skeleton
            variant="rounded"
            className="flex-1"
            height={width === 'w-full' ? 295 : 458}
          />
          <Skeleton
            variant="rounded"
            className="flex-1"
            height={width === 'w-full' ? 295 : 458}
          />
          <Skeleton
            variant="rounded"
            className="flex-1"
            height={width === 'w-full' ? 295 : 458}
          />
          <Skeleton
            variant="rounded"
            className="flex-1"
            height={width === 'w-full' ? 295 : 458}
          />
        </div>
      </div>
      <Skeleton
        variant="rounded"
        className="mt-16 mb-6"
        width="100%"
        height={1}
      />
      <div className={`flex justify-between ${width}`}>
        <Skeleton variant="rectangular" width={100} height={22} />
        <Skeleton variant="rounded" width={96} height={40} />
      </div>
    </div>
  );
};

const ServiceCard = (
  props: ServiceType & {
    selected: boolean;
    index: number;
    handleAdd: (id: string, price: number) => void;
    hiddenContent?: boolean;
  },
) => {
  const {
    name,
    id,
    content,
    price,
    promotion: { discount },
    selected,
    index,
    handleAdd,
    hiddenContent = false,
  } = props;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!selected) {
      trackClickEvent(e);
    }
    handleAdd(id, price);
  };

  return (
    <div
      className={
        hiddenContent ? 'h-[295px] w-full relative' : 'h-auto shrink-0 flex-1'
      }
    >
      <div
        key={name}
        className={`px-6 py-7.5 max-w-[280px] bg-white rounded-lg shadow-[0px_50px_80px_0px_#0000000d] ${hiddenContent ? 'absolute left-0 w-full group' : 'h-full relative'}`}
      >
        {GOODS_IMAGE[index]}
        <p className="text-2xl font-semibold mt-8 mb-4">{name}</p>
        <p className="text-sm mb-8">
          <span className="font-medium mr-2">¥{price}</span>
          <span className="line-through">¥{price + discount}</span>
        </p>
        <div
          className={
            hiddenContent
              ? 'max-h-0 mb-8 overflow-hidden transition-all duration-300 group-hover:max-h-36 group-hover:mb-13'
              : 'max-h-max mb-16'
          }
        >
          {content?.map((item) => (
            <div key={item} className="flex items-center text-sm mb-3">
              <DoneIcon className="!text-green-500" />
              <span className="ml-1">{item}</span>
            </div>
          ))}
        </div>
        <Button
          data-selected={selected}
          startIcon={selected ? <CheckRoundedIcon /> : <AddAroundIcon />}
          color="inherit"
          className="!rounded-full !bg-gray-100 !px-2.5 !py-1.5 !font-semibold !absolute bottom-7.5 right-6 data-[selected=true]:!bg-web-blue-500 data-[selected=true]:text-white"
          onClick={handleClick}
          data-ele-id={`goods_${name}`}
          data-ele-group={RESUME_CONSULT_SERVICE_ID}
        >
          {selected ? '已添加' : '添加'}
        </Button>
      </div>
    </div>
  );
};

const Services = (props: {
  handleGoods: (id: string[]) => void;
  width?: string;
  hiddenContent?: boolean;
  dataEleGroup?: string;
}) => {
  const {
    handleGoods,
    width = 'tablet:w-[1192px]',
    hiddenContent,
    dataEleGroup = RESUME_CONSULT_SERVICE_ID,
  } = props;

  const [services, setServices] = React.useState<ServiceType[]>([]);
  const [selectedService, setSelectedService] = React.useState<
    Pick<ServiceType, 'id' | 'price'>[]
  >([]);

  const handleAdd = (id: string, price: number) => {
    if (selectedService.findIndex((item) => item.id === id) !== -1) {
      setSelectedService(selectedService.filter((item) => item.id !== id));
    } else {
      setSelectedService([...selectedService, { id, price }]);
    }
  };

  const handleBookMentor = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (selectedService.length > 0) {
      trackClickEvent(e);
      handleGoods(selectedService.map((item) => item.id));
    } else {
      setAlertValue({ msg: '请至少选择一项商品' });
    }
  };

  React.useEffect(() => {
    getResumeConsultGoods()
      .then((res) => {
        if (res.status === 200) {
          setServices(res.data);
        }
      })
      .catch(() => setAlertValue({ msg: '商品列表请求失败，请重试' }));
  }, []);

  if (!services || services.length <= 0) {
    return <Loading width={width} />;
  }

  return (
    <div
      data-ele-id="content"
      data-ele-group={RESUME_CONSULT_SERVICE_ID}
      data-observer-id={RESUME_CONSULT_SERVICE_ID}
    >
      <div
        className={`w-full mx-auto overflow-x-auto px-2 ${width} tablet:px-0 tablet:overflow-x-visible`}
        id={RESUME_CONSULT_SERVICE_ID}
      >
        <div className="w-full flex justify-center mb-16 gap-x-6 mb-16 min-w-[1000px]">
          {services.map((item, index) => (
            <ServiceCard
              {...item}
              key={index + item.id}
              index={index}
              selected={!!selectedService.find(({ id }) => item.id === id)}
              handleAdd={handleAdd}
              hiddenContent={hiddenContent}
            />
          ))}
        </div>
      </div>
      <div
        className={`flex justify-between items-center pt-6 border-t border-black/20 mx-auto ${width}`}
      >
        <span className="text-xl leading-5.5 font-semibold">
          {selectedService.length > 0 &&
            `总计价格：${selectedService
              .map(({ price }) => price)
              .reduce((prev, cur) => prev + cur, 0)}`}
        </span>
        <Badge
          color="error"
          badgeContent={selectedService.length}
          max={services.length}
        >
          <Button
            variant="contained"
            className="!h-10"
            onClick={handleBookMentor}
            data-ele-id="btn_立即购买"
            data-ele-group={dataEleGroup}
          >
            立即购买
          </Button>
        </Badge>
      </div>
    </div>
  );
};

export default Services;
